import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPreSignedUrlToUpload } from '../../../../../../../redux/Spoolsbase/saga';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { getRepairStripInsertionSummary } from '../../integratedRepairStrip';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import type { WeldJoint, WeldStepPanelItem } from '../../SpoolsProduction';
import { Button } from '../../../../../../../components/ui/button';
import { Input } from '../../../../../../../components/ui/input';
import { AntdDate } from '../../../../../../../components/ui/antd-date';
import { Label } from '../../../../../../../components/ui/label';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../../../constants/fileUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../components/ui/select';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { SearchableSelect } from '../../../../../../../components/ui/SearchableSelect';
import { FileUpload } from '../../../../../../../components/ui/FileUpload';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../../../components/ui/dialog';
import {
  FieldGroup,
  FormGrid,
  SectionTitle,
} from '../weld/spoolDetailFormUi';
import {
  fileListToMeta,
  mergeSpoolStepDocumentsByS3Key,
  normalizeSpoolStepDocumentsForSave,
  parseDocumentsFromStepPayload,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import { SpoolStepSavedDocumentsList } from '../weld/SpoolStepSavedDocumentsList';
import {
  getAllNdtPanelRows,
  resolveNdtRetestStepPanelRowId,
  weldPanelsRevision,
} from '../weld/weldStepPanelHydration';
import { Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import type { DocumentTag } from '@/features/project/types';
import type { SpoolModalWorkflowStep } from '@/features/project/components/dashboard/spooltracker/repairWorkflowHelpers';

export type NDTSubmissionMode = 'draft' | 'submit';

const NDT_METHOD_ORDER = ['rt', 'ut', 'mpi'] as const;
export type NdtMethodValue = (typeof NDT_METHOD_ORDER)[number];

const NDT_METHOD_LABEL: Record<NdtMethodValue, string> = {
  rt: 'RT',
  ut: 'UT',
  mpi: 'MPI',
};

function ndtPayloadField(pl: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (k in pl && pl[k] != null) return pl[k];
  }
  return undefined;
}

function normalizeNdtMethod(raw: unknown): NdtMethodValue {
  const s = String(raw ?? 'rt').toLowerCase();
  if (s === 'ut' || s.includes('ultrasonic')) return 'ut';
  if (s === 'mpi' || s.includes('magnetic') || s.includes('mt')) return 'mpi';
  return 'rt';
}

/** Repair-cycle index persisted on NDT step payloads (`repairNo` / `repair_no`). */
function ndtPayloadRepairNo(pl: unknown): number | null {
  if (!pl || typeof pl !== 'object') return null;
  const o = pl as Record<string, unknown>;
  const rn = o.repairNo ?? o.repair_no;
  if (rn == null || rn === '') return null;
  const n = Number(rn);
  return Number.isFinite(n) ? n : null;
}

function isNdtPayloadRetestPhase(pl: unknown): boolean {
  if (!pl || typeof pl !== 'object') return false;
  const o = pl as Record<string, unknown>;
  return String(o.ndtPhase ?? o.ndt_phase ?? '').toLowerCase() === 'retest';
}

/** Latest primary (non-repair) NDT payload — avoids leaking repair retest rows into the standard strip. */
function getLastPrimaryNdtPanelPayload(weld: WeldJoint | null): Record<string, unknown> | null {
  const rows = getAllNdtPanelRows(weld);
  for (let i = rows.length - 1; i >= 0; i--) {
    const pl = rows[i]?.payload;
    if (!pl || typeof pl !== 'object') continue;
    const p = pl as Record<string, unknown>;
    if (ndtPayloadRepairNo(p) != null) continue;
    if (isNdtPayloadRetestPhase(p)) continue;
    return p;
  }
  return null;
}

function methodFromRowPayload(row: WeldStepPanelItem | null | undefined): NdtMethodValue {
  const pl = row?.payload;
  if (!pl || typeof pl !== 'object') return 'rt';
  const p = pl as Record<string, unknown>;
  return normalizeNdtMethod(ndtPayloadField(p, 'ndtMethod', 'ndt_method'));
}

export interface NdtIndicationRowPayload {
  id: number;
  location: string;
  distance: string;
  type: string;
  length: string;
  disposition: string;
}

/** NDT attachments after S3 upload via presign (`getPresignedUrlToUploadSpoolsbaseDoc`). */
export interface NdtUploadedDocumentPayload {
  fileName: string;
  s3Key: string;
  uploadedBy: string;
  documentCategory?: string;
  /** Tag ids (strings) per file; aligned with equipment document tags. */
  tags?: string[];
  spoolDocId?: number;
}

export interface NDTSavePayload {
  mode: NDTSubmissionMode;
  /** API parity with other spool weld-step saves (`draft` | `submit`). */
  submissionMode?: NDTSubmissionMode;
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  ndtMethod: string;
  reportNumber: string;
  testDate: string;
  equipmentCalRef: string;
  result: string;
  buyerWitnessed: string;
  indications: NdtIndicationRowPayload[];
  notes: string;
  attachmentsMeta: ReturnType<typeof fileListToMeta>;
  /** Row ids the user removed locally; include on save if your API supports removal. */
  deletedNdtRowIds?: number[];
  /** When set, the save updates this existing step row instead of (or in addition to) a new NDT. */
  editingNdtRowId?: number;
  ndtPhase?: 'standard' | 'retest';
  repairNo?: number | null;
  /** New uploads from this session (after presigned PUT to S3). */
  documents?: NdtUploadedDocumentPayload[];
}

interface Indication {
  id: number;
  location: string;
  distance: string;
  type: string;
  length: string;
  disposition: string;
}

const DEFAULT_NDT_LOCATION_LABELS = ["—", "12 o'clock", "6 o'clock", "3 o'clock", "9 o'clock"] as const;
const DEFAULT_NDT_TYPE_LABELS = ['—', 'Porosity', 'Slag inclusion', 'Lack of fusion', 'Crack'] as const;
const DEFAULT_NDT_DISPOSITION_LABELS = ['Acceptable', 'Repair required', 'Reject'] as const;

function normalizeNdtLocationDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return '—';
  const r = String(raw).trim();
  if (r === 'none' || r === '—') return '—';
  const lower = r.toLowerCase();
  if (r === '12' || lower === '12_oclock') return "12 o'clock";
  if (r === '6' || lower === '6_oclock') return "6 o'clock";
  if (r === '3' || lower === '3_oclock') return "3 o'clock";
  if (r === '9' || lower === '9_oclock') return "9 o'clock";
  return r;
}

function normalizeNdtTypeDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return '—';
  const r = String(raw).trim();
  if (r === 'none' || r === '—') return '—';
  const lower = r.toLowerCase().replace(/\s+/g, '_');
  if (lower === 'porosity') return 'Porosity';
  if (lower === 'slag' || lower === 'slag_inclusion') return 'Slag inclusion';
  if (lower === 'lof' || lower === 'lack_of_fusion') return 'Lack of fusion';
  if (lower === 'crack') return 'Crack';
  if ((DEFAULT_NDT_TYPE_LABELS as readonly string[]).includes(r)) return r;
  return r;
}

function normalizeNdtDispositionDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return 'Acceptable';
  const r = String(raw).trim().toLowerCase();
  if (r === 'acceptable') return 'Acceptable';
  if (r === 'repair') return 'Repair required';
  if (r === 'reject') return 'Reject';
  return String(raw).trim();
}

const DEFAULT_NDT_INDICATIONS: Indication[] = [
  { id: 1, location: '—', distance: '', type: '—', length: '', disposition: 'Acceptable' },
];

export interface NDTPanelProps {
  weld?: WeldJoint | null;
  context?: SpoolStepPanelContext;
  /** Second NDT pill on the integrated repair strip (after repair + re-VT). */
  ndtPhase?: 'standard' | 'retest';
  onNdtSave?: (payload: NDTSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  onNavigateToStep?: (step: SpoolModalWorkflowStep) => void;
  onOpenRaiseNcr?: () => void;
  /** Starts integrated repair strip from an NDT NCR row (same workflow table). */
  onAddRepairWeld?: (row: NcrRow) => void;
  repairNo?: number | null;
}

function firstAvailableNdtMethod(used: Set<NdtMethodValue>): NdtMethodValue {
  for (const m of NDT_METHOD_ORDER) {
    if (!used.has(m)) return m;
  }
  return 'rt';
}

export function NDTPanel({
  weld,
  context,
  ndtPhase = 'standard',
  repairNo = null,
  onNdtSave,
  onSubmittedNcrClick,
  onNavigateToStep,
  onOpenRaiseNcr,
  onAddRepairWeld,
}: NDTPanelProps) {
  /** Integrated repair strip “NDT retest” pill — scope rows + hide main NCR table (not inferred `repairNo` on primary tab). */
  const isNdtRetestStrip = ndtPhase === 'retest';
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, { matchStepType: 'ndt' }),
    [ncrListAll, weld?.id, context?.weldId]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const [ndtMethod, setNdtMethod] = useState<NdtMethodValue>('rt');
  const [reportNumber, setReportNumber] = useState('');
  const [testDate, setTestDate] = useState('');
  const [equipmentCalRef, setEquipmentCalRef] = useState('');
  const [result, setResult] = useState('pass');
  const [buyerWitnessed, setBuyerWitnessed] = useState('no');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [isAddingDocTag, setIsAddingDocTag] = useState(false);
  const [indications, setIndications] = useState<Indication[]>([...DEFAULT_NDT_INDICATIONS]);
  const [activeSubmit, setActiveSubmit] = useState<NDTSubmissionMode | null>(null);
  const activeSubmitRef = useRef<NDTSubmissionMode | null>(null);
  const [localRemovedRowIds, setLocalRemovedRowIds] = useState<number[]>([]);
  const [selectedSubmittedId, setSelectedSubmittedId] = useState<number | null>(null);
  const [deleteRowId, setDeleteRowId] = useState<number | null>(null);
  const [saveWithDeletes, setSaveWithDeletes] = useState<{
    open: boolean;
    mode: NDTSubmissionMode | null;
  }>({ open: false, mode: null });
  const [extraNdtLocationLabels, setExtraNdtLocationLabels] = useState<string[]>([]);
  const [extraNdtTypeLabels, setExtraNdtTypeLabels] = useState<string[]>([]);
  const [extraNdtDispositionLabels, setExtraNdtDispositionLabels] = useState<string[]>([]);
  const [isAddingNdtLocation, setIsAddingNdtLocation] = useState(false);
  const [isAddingNdtType, setIsAddingNdtType] = useState(false);
  const [isAddingNdtDisposition, setIsAddingNdtDisposition] = useState(false);
  const [ndtLocationTargetRowId, setNdtLocationTargetRowId] = useState<number | null>(null);
  const [ndtTypeTargetRowId, setNdtTypeTargetRowId] = useState<number | null>(null);
  const [ndtDispositionTargetRowId, setNdtDispositionTargetRowId] = useState<number | null>(null);
  const prevWeldIdRef = useRef<string | undefined>(undefined);

  const ndtRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'ndt')
  );
  const authUser = useSelector((s: { auth?: { user?: { email?: string } } }) => s.auth?.user);

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  useEffect(() => {
    let cancelled = false;
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (!cancelled && tags && Array.isArray(tags)) {
          setDocumentTags(tags);
        }
      } catch {
        /* ignore */
      }
    };
    void loadTags();
    return () => {
      cancelled = true;
    };
  }, []);

  const tagOptions = useMemo(
    () =>
      documentTags
        .filter((tag) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS')
        .map((tag) => ({
          value: tag.tags_id.toString(),
          label: tag.tags_name,
        })),
    [documentTags]
  );

  const serverNdtRows = useMemo(() => {
    const all = weld ? getAllNdtPanelRows(weld) : [];
    if (isNdtRetestStrip) {
      const rn = repairNo != null ? Number(repairNo) : NaN;
      if (!Number.isFinite(rn)) return [];
      return all.filter((r) => {
        const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
        return ndtPayloadRepairNo(pl) === rn;
      });
    }
    return all.filter((r) => {
      const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
      if (ndtPayloadRepairNo(pl) != null) return false;
      if (isNdtPayloadRetestPhase(pl)) return false;
      return true;
    });
  }, [weld, weld?.id, weldPanelsRevision(weld ?? null), isNdtRetestStrip, repairNo]);

  const visibleSubmittedRows = useMemo(
    () => serverNdtRows.filter((r) => !localRemovedRowIds.includes(r.id)),
    [serverNdtRows, localRemovedRowIds]
  );

  const usedNdtMethods = useMemo(() => {
    const s = new Set<NdtMethodValue>();
    for (const row of visibleSubmittedRows) {
      s.add(methodFromRowPayload(row));
    }
    return s;
  }, [visibleSubmittedRows]);

  const allNdtMethodSlotsUsed = usedNdtMethods.size >= NDT_METHOD_ORDER.length;
  const canAddNewNdt = !allNdtMethodSlotsUsed;
  const selectedRow = useMemo(
    () => visibleSubmittedRows.find((r) => r.id === selectedSubmittedId) ?? null,
    [visibleSubmittedRows, selectedSubmittedId]
  );
  const isEditingSelectedRow = selectedSubmittedId != null;
  const isMethodTakenByOtherRow = useCallback(
    (m: NdtMethodValue) =>
      visibleSubmittedRows.some(
        (r) => r.id !== selectedSubmittedId && methodFromRowPayload(r) === m
      ),
    [visibleSubmittedRows, selectedSubmittedId]
  );
  const currentMethodBlocked = useMemo(
    () =>
      !isEditingSelectedRow && allNdtMethodSlotsUsed && usedNdtMethods.has(ndtMethod),
    [isEditingSelectedRow, allNdtMethodSlotsUsed, usedNdtMethods, ndtMethod]
  );

  const hydrateFormFromLastPayload = useCallback((pl: Record<string, unknown>) => {
    const m = ndtPayloadField(pl, 'ndtMethod', 'ndt_method');
    if (m != null) setNdtMethod(normalizeNdtMethod(m));
    const rn = ndtPayloadField(pl, 'reportNumber', 'report_number', 'report_no');
    if (rn != null) setReportNumber(String(rn));
    const td = ndtPayloadField(pl, 'testDate', 'test_date', 'ndtDate');
    if (td != null) setTestDate(String(td));
    const ec = ndtPayloadField(pl, 'equipmentCalRef', 'equipment_cal_ref', 'cal_ref');
    if (ec != null) setEquipmentCalRef(String(ec));
    const res = ndtPayloadField(pl, 'result', 'ndt_result');
    if (res != null) setResult(String(res));
    const bw = ndtPayloadField(pl, 'buyerWitnessed', 'buyer_witnessed', 'buyer_witness');
    if (bw != null) setBuyerWitnessed(String(bw));
    const n = ndtPayloadField(pl, 'notes', 'remarks');
    if (n != null) setNotes(String(n));
    if (Array.isArray(pl.indications) && pl.indications.length > 0) {
      const locExtras: string[] = [];
      const typeExtras: string[] = [];
      const dispExtras: string[] = [];
      const normLoc = (raw: string) => {
        const d = normalizeNdtLocationDisplay(raw);
        if (
          d &&
          !(DEFAULT_NDT_LOCATION_LABELS as readonly string[]).includes(d) &&
          !locExtras.includes(d)
        ) {
          locExtras.push(d);
        }
        return d;
      };
      const normTyp = (raw: string) => {
        const d = normalizeNdtTypeDisplay(raw);
        if (
          d &&
          !(DEFAULT_NDT_TYPE_LABELS as readonly string[]).includes(d) &&
          !typeExtras.includes(d)
        ) {
          typeExtras.push(d);
        }
        return d;
      };
      const normDisp = (raw: string) => {
        const d = normalizeNdtDispositionDisplay(raw);
        if (
          d &&
          !(DEFAULT_NDT_DISPOSITION_LABELS as readonly string[]).includes(d) &&
          !dispExtras.includes(d)
        ) {
          dispExtras.push(d);
        }
        return d;
      };
      setExtraNdtLocationLabels((prev) => Array.from(new Set([...prev, ...locExtras])));
      setExtraNdtTypeLabels((prev) => Array.from(new Set([...prev, ...typeExtras])));
      setExtraNdtDispositionLabels((prev) => Array.from(new Set([...prev, ...dispExtras])));
      setIndications(
        (pl.indications as NdtIndicationRowPayload[]).map((r, i) => ({
          id: Number(r.id) || i + 1,
          location: normLoc(r.location != null ? String(r.location) : ''),
          distance: r.distance != null ? String(r.distance) : '',
          type: normTyp(r.type != null ? String(r.type) : ''),
          length: r.length != null ? String(r.length) : '',
          disposition: normDisp(r.disposition != null ? String(r.disposition) : ''),
        }))
      );
    } else {
      setIndications([...DEFAULT_NDT_INDICATIONS]);
      setExtraNdtLocationLabels([]);
      setExtraNdtTypeLabels([]);
      setExtraNdtDispositionLabels([]);
    }
  }, []);

  const resetNewNdtFormDefaults = useCallback((used: Set<NdtMethodValue>) => {
    setReportNumber('');
    setTestDate('');
    setEquipmentCalRef('');
    setResult('pass');
    setBuyerWitnessed('no');
    setNotes('');
    setFiles([]);
    setIndications([...DEFAULT_NDT_INDICATIONS]);
    setExtraNdtLocationLabels([]);
    setExtraNdtTypeLabels([]);
    setExtraNdtDispositionLabels([]);
    setNdtMethod(firstAvailableNdtMethod(used));
  }, []);

  useEffect(() => {
    if (!weld) {
      setNdtMethod('rt');
      setReportNumber('');
      setTestDate('');
      setEquipmentCalRef('');
      setResult('pass');
      setBuyerWitnessed('no');
      setNotes('');
      setFiles([]);
      setSelectedTags([]);
      setIndications([...DEFAULT_NDT_INDICATIONS]);
      setExtraNdtLocationLabels([]);
      setExtraNdtTypeLabels([]);
      setExtraNdtDispositionLabels([]);
      setLocalRemovedRowIds([]);
      setSelectedSubmittedId(null);
      prevWeldIdRef.current = undefined;
      return;
    }

    const jointSwitched = prevWeldIdRef.current !== weld.id;
    if (jointSwitched) {
      setLocalRemovedRowIds([]);
      setSelectedSubmittedId(null);
      prevWeldIdRef.current = weld.id;
    }

    const allRows = serverNdtRows;
    const pl = getLastPrimaryNdtPanelPayload(weld) ?? {};
    if (allRows.length > 0 && selectedSubmittedId != null) {
      return;
    }
    if (jointSwitched) {
      setFiles([]);
      setSelectedTags([]);
    }
    if (allRows.length === 0) {
      if (isNdtRetestStrip) {
        setNdtMethod('rt');
        setReportNumber('');
        setTestDate('');
        setEquipmentCalRef('');
        setResult('pass');
        setBuyerWitnessed('no');
        setIndications([...DEFAULT_NDT_INDICATIONS]);
        setExtraNdtLocationLabels([]);
        setExtraNdtTypeLabels([]);
        setExtraNdtDispositionLabels([]);
        setFiles([]);
        setSelectedTags([]);
        setNotes('');
        return;
      }
      if (Object.keys(pl).length > 0) {
        hydrateFormFromLastPayload(pl);
        setFiles([]);
        setSelectedTags([]);
      } else {
        setNdtMethod('rt');
        setReportNumber('');
        setTestDate('');
        setEquipmentCalRef('');
        setResult('pass');
        setBuyerWitnessed('no');
        setIndications([...DEFAULT_NDT_INDICATIONS]);
        setExtraNdtLocationLabels([]);
        setExtraNdtTypeLabels([]);
        setExtraNdtDispositionLabels([]);
        setFiles([]);
        setSelectedTags([]);
        setNotes('');
      }
      return;
    }

    setFiles([]);
    setSelectedTags([]);
    const used = new Set<NdtMethodValue>(allRows.map((r) => methodFromRowPayload(r)));
    resetNewNdtFormDefaults(used);
  }, [
    weld,
    weld?.id,
    weldPanelsRevision(weld ?? null),
    selectedSubmittedId,
    hydrateFormFromLastPayload,
    resetNewNdtFormDefaults,
    serverNdtRows,
    isNdtRetestStrip,
  ]);

  useEffect(() => {
    setSelectedSubmittedId((cur) => {
      if (cur == null) return null;
      return serverNdtRows.some((r) => r.id === cur) ? cur : null;
    });
  }, [serverNdtRows]);

  useEffect(() => {
    if (!weld || selectedSubmittedId == null) return;
    const match = serverNdtRows.find((r) => r.id === selectedSubmittedId);
    if (match?.payload && typeof match.payload === 'object') {
      setFiles([]);
      setSelectedTags([]);
      hydrateFormFromLastPayload(match.payload as Record<string, unknown>);
    }
  }, [selectedSubmittedId, weld, weld?.id, serverNdtRows, hydrateFormFromLastPayload]);

  useEffect(() => {
    if (!weld) return;
    if (selectedSubmittedId != null) return;
    if (allNdtMethodSlotsUsed) return;
    if (usedNdtMethods.has(ndtMethod)) {
      setNdtMethod(firstAvailableNdtMethod(usedNdtMethods));
    }
  }, [weld, allNdtMethodSlotsUsed, usedNdtMethods, ndtMethod, selectedSubmittedId]);

  useEffect(() => {
    if (!weld) return;
    setLocalRemovedRowIds((ids) => {
      const have = new Set(serverNdtRows.map((r) => r.id));
      return ids.filter((id) => have.has(id));
    });
  }, [weld, serverNdtRows]);

  const handleMaterialFileSelect = (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      setFiles((prev) => [...prev, ...fileArray]);
      setSelectedTags((prev) => [...prev, ...fileArray.map(() => [] as string[])]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleAddDocTag = async (tagName: string) => {
    const v = tagName.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.error('NDT', 'Project context required to add tags.');
      return;
    }
    if (
      documentTags.some(
        (tag) =>
          tag.tags_name === v && tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS'
      )
    ) {
      Notification.warning('NDT', `${v} tag already exists`);
      return;
    }
    const res = await AddMaterialCategory({
      tags_name: v,
      project_id: Number(pid),
      tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
      created_by: authUser?.email || '',
    });
    if (res) {
      setDocumentTags((prev) => [...prev, res]);
      setIsAddingDocTag(false);
    }
  };

  const addIndication = () =>
    setIndications((prev) => [
      ...prev,
      {
        id: Date.now(),
        location: '—',
        distance: '',
        type: '—',
        length: '',
        disposition: 'Acceptable',
      },
    ]);

  const removeIndication = (id: number) =>
    setIndications((prev) => prev.filter((r) => r.id !== id));

  const updateIndication = (id: number, field: keyof Indication, value: string) =>
    setIndications((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const ndtLocationSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_NDT_LOCATION_LABELS) {
      map.set(name, { id: name, name });
    }
    for (const name of extraNdtLocationLabels) {
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    for (const row of indications) {
      if (row.location && !map.has(row.location)) {
        map.set(row.location, { id: row.location, name: row.location });
      }
    }
    return Array.from(map.values());
  }, [extraNdtLocationLabels, indications]);

  const ndtTypeSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_NDT_TYPE_LABELS) {
      map.set(name, { id: name, name });
    }
    for (const name of extraNdtTypeLabels) {
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    for (const row of indications) {
      if (row.type && !map.has(row.type)) {
        map.set(row.type, { id: row.type, name: row.type });
      }
    }
    return Array.from(map.values());
  }, [extraNdtTypeLabels, indications]);

  const ndtDispositionSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_NDT_DISPOSITION_LABELS) {
      map.set(name, { id: name, name });
    }
    for (const name of extraNdtDispositionLabels) {
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    for (const row of indications) {
      if (row.disposition && !map.has(row.disposition)) {
        map.set(row.disposition, { id: row.disposition, name: row.disposition });
      }
    }
    return Array.from(map.values());
  }, [extraNdtDispositionLabels, indications]);

  const handleAddNdtLocation = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    setExtraNdtLocationLabels((p) => (p.includes(v) ? p : [...p, v]));
    if (ndtLocationTargetRowId != null) {
      setIndications((prev) =>
        prev.map((r) => (r.id === ndtLocationTargetRowId ? { ...r, location: v } : r))
      );
    }
    setNdtLocationTargetRowId(null);
    setIsAddingNdtLocation(false);
  };

  const handleAddNdtType = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    setExtraNdtTypeLabels((p) => (p.includes(v) ? p : [...p, v]));
    if (ndtTypeTargetRowId != null) {
      setIndications((prev) =>
        prev.map((r) => (r.id === ndtTypeTargetRowId ? { ...r, type: v } : r))
      );
    }
    setNdtTypeTargetRowId(null);
    setIsAddingNdtType(false);
  };

  const handleAddNdtDisposition = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    setExtraNdtDispositionLabels((p) => (p.includes(v) ? p : [...p, v]));
    if (ndtDispositionTargetRowId != null) {
      setIndications((prev) =>
        prev.map((r) => (r.id === ndtDispositionTargetRowId ? { ...r, disposition: v } : r))
      );
    }
    setNdtDispositionTargetRowId(null);
    setIsAddingNdtDisposition(false);
  };

  const buildPayload = useCallback(
    (mode: NDTSubmissionMode): NDTSavePayload => {
      const effectiveEditingNdtRowId =
        selectedSubmittedId ??
        (ndtPhase === 'retest'
          ? resolveNdtRetestStepPanelRowId(weld ?? null, repairNo)
          : undefined);
      const insertNdtRetestRow =
        ndtPhase === 'retest' && effectiveEditingNdtRowId == null;
      return {
        mode,
        submissionMode: mode,
        projectId: context?.projectId ?? null,
        pipeId: context?.pipeId ?? null,
        spoolId: context?.spoolId ?? null,
        spoolName: context?.spoolName ?? null,
        weldId: context?.weldId ?? null,
        weldNumber: context?.weldNumber ?? null,
        ndtMethod,
        reportNumber: reportNumber.trim(),
        testDate,
        equipmentCalRef: equipmentCalRef.trim(),
        result,
        buyerWitnessed,
        indications: indications.map(({ id, location, distance, type, length, disposition }) => ({
          id,
          location,
          distance,
          type,
          length,
          disposition,
        })),
        notes: notes.trim(),
        attachmentsMeta: fileListToMeta(files),
        ...(localRemovedRowIds.length > 0
          ? { deletedNdtRowIds: [...localRemovedRowIds] }
          : {}),
        ...(effectiveEditingNdtRowId != null
          ? { editingNdtRowId: effectiveEditingNdtRowId }
          : {}),
        ...(insertNdtRetestRow ? { ndtPhase: 'retest' as const } : {}),
        ...(repairNo != null && !Number.isNaN(Number(repairNo))
          ? { repairNo: Number(repairNo) }
          : {}),
      };
    },
    [
      context,
      weld,
      ndtMethod,
      reportNumber,
      testDate,
      equipmentCalRef,
      result,
      buyerWitnessed,
      indications,
      notes,
      files,
      localRemovedRowIds,
      selectedSubmittedId,
      ndtPhase,
      repairNo,
    ]
  );

  const runSave = async (mode: NDTSubmissionMode) => {
    if (!onNdtSave) return;

    let newUploadDocuments: NdtUploadedDocumentPayload[] | undefined;

    if (files.length > 0) {
      const projectId = Number(context?.projectId);
      const pipeId = Number(context?.pipeId);
      const projectTypeId = Number(context?.projectTypeId);
      const spoolId = Number(context?.spoolId);
      if (
        !Number.isFinite(projectId) ||
        !Number.isFinite(pipeId) ||
        !Number.isFinite(projectTypeId) ||
        !Number.isFinite(spoolId)
      ) {
        Notification.error(
          'NDT',
          'Missing project, pipe, spool, or project type — cannot upload documents. Open this weld from a spool context.'
        );
        return;
      }

      const uploadPayload = {
        projectId,
        pipeId,
        spoolId,
        projectTypeId,
        type: 'NDT',
      };

      const s3Response = await getPreSignedUrlToUpload(files, uploadPayload, 1);

      if (!s3Response || s3Response.length === 0) {
        Notification.error('NDT', 'Document upload failed. Please try again.');
        return;
      }

      const uploadedBy = authUser?.email?.trim() || '';
      newUploadDocuments = s3Response.map((f: { fileName: string; s3Key: string }, index: number) => ({
        fileName: f.fileName,
        s3Key: f.s3Key,
        uploadedBy,
        documentCategory: 'NDT',
        tags: selectedTags[index] ?? [],
      }));
    }

    const mergedDocuments =
      newUploadDocuments != null && newUploadDocuments.length > 0
        ? mergeSpoolStepDocumentsByS3Key(ndtPersistedDocuments, newUploadDocuments)
        : ndtPersistedDocuments;

    const payload: NDTSavePayload = {
      ...buildPayload(mode),
      ...(mergedDocuments.length > 0
        ? { documents: normalizeSpoolStepDocumentsForSave(mergedDocuments) }
        : {}),
    };

    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    setFiles([]);
    setSelectedTags([]);
    await Promise.resolve(onNdtSave(payload));
  };

  const requestSave = (mode: NDTSubmissionMode) => {
    if (localRemovedRowIds.length > 0) {
      setSaveWithDeletes({ open: true, mode });
    } else {
      void runSave(mode);
    }
  };

  const handleConfirmDeleteRow = () => {
    if (deleteRowId == null) return;
    setLocalRemovedRowIds((prev) => (prev.includes(deleteRowId) ? prev : [...prev, deleteRowId]));
    if (selectedSubmittedId === deleteRowId) setSelectedSubmittedId(null);
    setDeleteRowId(null);
  };

  const handleConfirmSaveWithDeletes = () => {
    if (saveWithDeletes.mode) void runSave(saveWithDeletes.mode);
    setSaveWithDeletes({ open: false, mode: null });
  };

  const submit = (mode: NDTSubmissionMode) => {
    requestSave(mode);
  };

  useEffect(() => {
    if (!ndtRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [ndtRemoteSaving]);

  const hasExistingNdt = useMemo(() => {
    return serverNdtRows.some(
      (r) => r.payload && typeof r.payload === 'object' && Object.keys(r.payload as object).length > 0
    );
  }, [serverNdtRows]);

  const hasVisibleSubmitted = visibleSubmittedRows.length > 0;
  const actionText = hasExistingNdt ? 'Update NDT Report' : 'Save NDT Report';
  const reportLoadingText = hasExistingNdt ? 'Updating…' : 'Saving…';
  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = ndtRemoteSaving && kind === 'draft';
  const savingReport = ndtRemoteSaving && kind === 'submit';
  const canAct = Boolean(weld?.id ?? context?.weldId);
  const newFormDisabled =
    !canAct ||
    (!isEditingSelectedRow && (!canAddNewNdt || currentMethodBlocked));
  /** Staged deletions are applied on save; allow save even when the new-NDT form is blocked. */
  const canTriggerSave =
    canAct &&
    (localRemovedRowIds.length > 0 ||
      !newFormDisabled ||
      isEditingSelectedRow);
  const draftDisabled = !canTriggerSave || savingReport;
  const reportDisabled = !canTriggerSave || savingDraft;

  const payloadForSelected = useMemo((): Record<string, unknown> | null => {
    if (!selectedRow?.payload || typeof selectedRow.payload !== 'object') return null;
    return selectedRow.payload as Record<string, unknown>;
  }, [selectedRow]);

  const ndtPersistedDocuments = useMemo(() => {
    if (!selectedRow?.payload || typeof selectedRow.payload !== 'object') return [];
    return parseDocumentsFromStepPayload(selectedRow.payload as Record<string, unknown>);
  }, [selectedRow]);

  const downloadContext = useMemo(() => {
    const projectId = Number(context?.projectId);
    const pipeId = Number(context?.pipeId);
    const projectTypeId = Number(context?.projectTypeId);
    const spoolId = Number(context?.spoolId);
    if (![projectId, pipeId, projectTypeId, spoolId].every(Number.isFinite)) return null;
    return { projectId, pipeId, projectTypeId, spoolId };
  }, [context?.projectId, context?.pipeId, context?.projectTypeId, context?.spoolId]);

  const showWorkflowNcrTable =
    !isNdtRetestStrip &&
    ncrList.length > 0 &&
    onSubmittedNcrClick &&
    context?.projectId != null &&
    context?.projectId !== '' &&
    context?.pipeId != null &&
    context?.pipeId !== '';

  return (
    <div className="space-y-4">
         <Card className="flex max-h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200">
         <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain p-4">
      {showWorkflowNcrTable ? (
        <WorkflowNcrSummaryTable
          ncrList={ncrList}
          loading={ncrListLoading}
          step="ndt"
          stepTitle="NDT"
          weldId={weld?.id ?? null}
          onRowClick={onSubmittedNcrClick}
          onAddRepairWeld={onAddRepairWeld}
          repairPlanHint={onAddRepairWeld ? getRepairStripInsertionSummary('ndt') : undefined}
          repairActionLabel="Start NDT repair"
        />
      ) : null}

      {isNdtRetestStrip ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          role="status"
        >
          <span className="font-semibold">Repair NDT (retest)</span>
          {repairNo != null && !Number.isNaN(Number(repairNo)) ? (
            <span>
              {' '}
              — repair <span className="font-semibold">#{Number(repairNo)}</span>. Only NDT rows saved with
              this index are listed; primary NDT and the NCR table are hidden on this step.
            </span>
          ) : (
            <span> — select a repair context with a valid repair number.</span>
          )}
        </div>
      ) : null}

      <SectionTitle>
        {isNdtRetestStrip
          ? `Submitted NDT — repair ${repairNo != null && !Number.isNaN(Number(repairNo)) ? `#${Number(repairNo)}` : '—'}`
          : 'Submitted NDT'}
      </SectionTitle>
      {hasVisibleSubmitted ? (
        <>
          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="w-full min-w-[640px] text-xs">
              <thead className="bg-gray-50 border-b text-gray-500 uppercase tracking-wide">
                <tr>
                  {['Method', 'Report #', 'Test date', 'Result', ''].map((h, i) => (
                    <th
                      key={`h-${h}-${i}`}
                      className="px-2 py-2 text-left font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleSubmittedRows.map((row) => {
                  const pl = row.payload && typeof row.payload === 'object' ? (row.payload as Record<string, unknown>) : null;
                  const m = pl ? methodFromRowPayload(row) : 'rt';
                  const rpt = pl ? String(ndtPayloadField(pl, 'reportNumber', 'report_number', 'report_no') ?? '—') : '—';
                  const td = pl
                    ? String(ndtPayloadField(pl, 'testDate', 'test_date', 'ndtDate') ?? '—')
                    : '—';
                  const res = pl
                    ? String(ndtPayloadField(pl, 'result', 'ndt_result', 'result') ?? '—')
                    : '—';
                  const sel = row.id === selectedSubmittedId;
                  return (
                    <tr
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedSubmittedId((cur) => (cur === row.id ? null : row.id))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedSubmittedId((cur) => (cur === row.id ? null : row.id));
                        }
                      }}
                      className={[
                        'border-b border-gray-100 last:border-0 cursor-pointer transition-colors',
                        sel ? 'bg-teal-50/80' : 'hover:bg-gray-50/80',
                      ].join(' ')}
                    >
                      <td className="px-2 py-2 font-medium text-slate-800">{NDT_METHOD_LABEL[m]}</td>
                      <td className="px-2 py-2 text-slate-700 max-w-[180px] truncate" title={rpt}>
                        {rpt}
                      </td>
                      <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{td}</td>
                      <td className="px-2 py-2 text-slate-700 capitalize">{res}</td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteRowId(row.id);
                          }}
                        >
                         <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedRow && (
            <div className="rounded-md border border-teal-200/80 bg-teal-50/40 px-3 py-2 text-sm text-slate-700">
              <span className="font-medium text-slate-900">Selected: </span>
              {NDT_METHOD_LABEL[methodFromRowPayload(selectedRow)]} — report{' '}
              {payloadForSelected
                ? String(
                    ndtPayloadField(
                      payloadForSelected,
                      'reportNumber',
                      'report_number',
                      'report_no'
                    ) ?? '—'
                  )
                : '—'}
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground rounded-md border border-dashed border-gray-200 px-3 py-2">
          {isNdtRetestStrip
            ? 'No NDT retest saved for this repair index yet. Use Save Draft below — new rows are tagged with this repair number.'
            : 'No NDT report submitted for this joint yet. Use the form below to add the first one.'}
        </p>
      )}

      {allNdtMethodSlotsUsed && !isEditingSelectedRow && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200/80 rounded-md px-3 py-2">
          RT, UT, and MPI are already in the list. Remove a row above if you need to replace or
          re-file an NDT type.
        </p>
      )}

      <Dialog open={deleteRowId != null} onOpenChange={(o) => !o && setDeleteRowId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove this NDT?</DialogTitle>
            <DialogDescription>
              It will be removed from the list on your next save. This cannot be undone in the
              app until you save.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteRowId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 text-white hover:bg-red-600/90"
              onClick={handleConfirmDeleteRow}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={saveWithDeletes.open}
        onOpenChange={(o) => !o && setSaveWithDeletes({ open: false, mode: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save with removals?</DialogTitle>
            <DialogDescription>
              You are saving while {localRemovedRowIds.length} submitted NDT row
              {localRemovedRowIds.length > 1 ? 's' : ''}{' '}
              {localRemovedRowIds.length > 1 ? 'are' : 'is'} marked for removal. The current
              new-report form and these removals will be sent together. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSaveWithDeletes({ open: false, mode: null })}
            >
              Cancel
            </Button>
            <Button type="button" className="bg-hero-red text-white hover:bg-hero-red-60" onClick={handleConfirmSaveWithDeletes}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-1">
        <SectionTitle>
          {isEditingSelectedRow
            ? 'NDT report (editing selected record)'
            : isNdtRetestStrip
              ? `New NDT report — repair ${
                  repairNo != null && !Number.isNaN(Number(repairNo)) ? `#${Number(repairNo)}` : ''
                }`.trim()
              : 'New NDT report'}
        </SectionTitle>
        {isEditingSelectedRow && (
          <p className="text-xs text-muted-foreground">
            The table above loads this form. Deselect the row to start a new report with an
            available NDT type.
          </p>
        )}
      </div>
      <FormGrid>
        <FieldGroup label="NDT Method" required>
          <Select
            value={ndtMethod}
            onValueChange={(v) => setNdtMethod(v as NdtMethodValue)}
            disabled={!canAct || (!isEditingSelectedRow && allNdtMethodSlotsUsed)}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NDT_METHOD_ORDER.map((m) => {
                const taken = isMethodTakenByOtherRow(m);
                const label =
                  m === 'rt'
                    ? 'RT — Radiographic Testing'
                    : m === 'ut'
                      ? 'UT — Ultrasonic Testing'
                      : 'MPI — Magnetic Particle';
                return (
                  <SelectItem key={m} value={m} disabled={taken}>
                    {label}
                    {taken ? ' (already in list)' : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Report Number" required>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. NDT-RT-2026-045"
            value={reportNumber}
            onChange={(e) => setReportNumber(e.target.value)}
            disabled={newFormDisabled}
          />
        </FieldGroup>
        <FieldGroup label="Test Date" required>
          <AntdDate
            className="h-9 text-sm"
            value={testDate}
            onChange={setTestDate}
            disabled={newFormDisabled}
          />
        </FieldGroup>
        <FieldGroup label="Equipment Cal Ref">
          <Input
            className="h-9 text-sm"
            placeholder="Cal cert number"
            value={equipmentCalRef}
            onChange={(e) => setEquipmentCalRef(e.target.value)}
            disabled={newFormDisabled}
          />
        </FieldGroup>
        <FieldGroup label="Result" required>
          <Select
            value={result}
            onValueChange={setResult}
            disabled={newFormDisabled}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pass">Pass — Acceptable</SelectItem>
              <SelectItem value="fail">Fail — Repair Required</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="BUYER Witnessed">
          <Select
            value={buyerWitnessed}
            onValueChange={setBuyerWitnessed}
            disabled={newFormDisabled}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      </FormGrid>
      <SectionTitle>Indications Found (if any)</SectionTitle>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b text-gray-500 uppercase tracking-wide">
            <tr>
              {['Location', 'Distance (mm)', 'Type', 'Length (mm)', 'Disposition', ''].map(
                (h, i) => (
                  <th key={`${h}-${i}`} className="px-2 py-2 text-left font-medium">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {indications.map((row) => (
              <tr key={row.id} className="border-b border-gray-100 last:border-0">
                <td className="px-2 py-1.5 min-w-[11rem]">
                  <SearchableSelect
                    value={row.location}
                    onValueChange={(v) => updateIndication(row.id, 'location', v)}
                    placeholder="Location"
                    searchPlaceholder="Search locations…"
                    options={ndtLocationSelectOptions}
                    onAddNew={() => {
                      setNdtLocationTargetRowId(row.id);
                      setIsAddingNdtLocation(true);
                    }}
                    addNewText="Add location"
                    disabled={newFormDisabled}
                    className="w-full max-w-[14rem] [&_button]:h-7 [&_button]:text-xs [&_button]:py-0"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    className="h-7 text-xs w-16"
                    placeholder="mm"
                    value={row.distance}
                    onChange={(e) => updateIndication(row.id, 'distance', e.target.value)}
                    disabled={newFormDisabled}
                  />
                </td>
                <td className="px-2 py-1.5 min-w-[11rem]">
                  <SearchableSelect
                    value={row.type}
                    onValueChange={(v) => updateIndication(row.id, 'type', v)}
                    placeholder="Type"
                    searchPlaceholder="Search types…"
                    options={ndtTypeSelectOptions}
                    onAddNew={() => {
                      setNdtTypeTargetRowId(row.id);
                      setIsAddingNdtType(true);
                    }}
                    addNewText="Add type"
                    disabled={newFormDisabled}
                    className="w-full max-w-[15rem] [&_button]:h-7 [&_button]:text-xs [&_button]:py-0"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    className="h-7 text-xs w-16"
                    placeholder="mm"
                    value={row.length}
                    onChange={(e) => updateIndication(row.id, 'length', e.target.value)}
                    disabled={newFormDisabled}
                  />
                </td>
                <td className="px-2 py-1.5 min-w-[11rem]">
                  <SearchableSelect
                    value={row.disposition}
                    onValueChange={(v) => updateIndication(row.id, 'disposition', v)}
                    placeholder="Disposition"
                    searchPlaceholder="Search disposition…"
                    options={ndtDispositionSelectOptions}
                    onAddNew={() => {
                      setNdtDispositionTargetRowId(row.id);
                      setIsAddingNdtDisposition(true);
                    }}
                    addNewText="Add disposition"
                    disabled={newFormDisabled}
                    className="w-full max-w-[14rem] [&_button]:h-7 [&_button]:text-xs [&_button]:py-0"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => removeIndication(row.id)}
                    disabled={newFormDisabled}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    title="Remove row"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={addIndication}
          disabled={newFormDisabled}
        >
          + Add Indication
        </Button>
      </div>

      {isAddingNdtLocation && (
        <AddNewItemDialog
          open={isAddingNdtLocation}
          onOpenChange={(o) => {
            setIsAddingNdtLocation(o);
            if (!o) setNdtLocationTargetRowId(null);
          }}
          title="Add NDT indication location"
          label="Location"
          placeholder="e.g. Custom clock position"
          onAdd={handleAddNdtLocation}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
      {isAddingNdtType && (
        <AddNewItemDialog
          open={isAddingNdtType}
          onOpenChange={(o) => {
            setIsAddingNdtType(o);
            if (!o) setNdtTypeTargetRowId(null);
          }}
          title="Add NDT indication type"
          label="Type"
          placeholder="e.g. Undercut"
          onAdd={handleAddNdtType}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
      {isAddingNdtDisposition && (
        <AddNewItemDialog
          open={isAddingNdtDisposition}
          onOpenChange={(o) => {
            setIsAddingNdtDisposition(o);
            if (!o) setNdtDispositionTargetRowId(null);
          }}
          title="Add NDT disposition"
          label="Disposition"
          placeholder="e.g. Engineering review"
          onAdd={handleAddNdtDisposition}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}

      <FieldGroup label="Notes / Observations">
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          rows={2}
          placeholder="Describe any observations..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={newFormDisabled}
        />
      </FieldGroup>

      <div className="space-y-2">
        <Label>Upload NDT Documents</Label>
        <p className="text-sm text-gray-600">
          Upload the NDT report and supporting documents (Film, UT scan data, or signed PDF).
          <br />
          <span className="text-xs text-gray-500">
            Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images
            (.jpg, .png, .gif)
            <br />
            Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
          </span>
        </p>

        {isEditingSelectedRow ? (
          <SpoolStepSavedDocumentsList
            documents={ndtPersistedDocuments}
            rowUpdatedAt={selectedRow?.updatedAt ?? null}
            downloadContext={downloadContext}
          />
        ) : null}

        <FileUpload
          onFileSelect={handleMaterialFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
          multiple={true}
          maxFiles={100}
          buttonText="Upload Documents"
          variant="outline"
          size="lg"
          onRemoveFile={handleRemoveFile}
          selectedFiles={files}
          showFileList={files.length > 0}
          className="w-full"
          showTags={true}
          optionsTags={tagOptions}
          selectedTags={selectedTags}
          showAddButton={true}
          addButtonText="Add New Tag"
          onAddButtonClick={() => setIsAddingDocTag(true)}
          onTagsChange={handleTagsChange}
          maxHeight=""
          disabled={newFormDisabled}
        />
        <div className="text-xs text-gray-500">Current files: {files.length} file(s)</div>
      </div>

      <AddNewItemDialog
        open={isAddingDocTag}
        onOpenChange={setIsAddingDocTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddDocTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      <div className="flex justify-end gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!canTriggerSave || savingDraft || savingReport}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={draftDisabled}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={() => void submit('draft')}
        >
          Save Draft
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-hero-red text-white hover:bg-hero-red-60"
          disabled={reportDisabled}
          loading={savingReport}
          loadingText={reportLoadingText}
          onClick={() => void submit('submit')}
        >
          {actionText}
        </Button>
      </div>
      </CardContent>
      </Card>
    </div>
  );
}
